import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Checkbox, PrimaryButton, SecondaryButton, SnackbarStatus, StatusBar } from '@weareopenr/component-library';
import { IMenuSetDetail } from 'src/api/getMenuSet';
import { IUpdateMenuSetRequest } from 'src/api/updateMenuSet';
import { ButtonContainer } from 'src/components/ButtonContainer/ButtonContainer';
import {
  MENU_SET_DETAILS_LABELS,
  MenuDetailsFields,
  UNIQUE_MENU_SET_NAME_ERROR,
} from 'src/components/MenuDetailsFields';
import { IFormData } from 'src/components/MenuModal/types';
import { useReportTrayDirty } from 'src/contexts/menuTray';
import { EntitySource } from 'src/constants';
import { findImageUrl } from 'src/hooks/useMenu/formatMenu/formatMenu';
import { useMenuSets } from 'src/hooks/useMenuSets';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useRanges } from 'src/hooks/useRanges/useRanges';
import { useTimetableRangesEnabled } from 'src/hooks/useTimetableRangesEnabled';
import { useUpdateMenuSet } from 'src/hooks/useUpdateMenuSet';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { ISavedImage, MenuSetImageField } from './MenuSetImageField';
import styles from './MenuSetDetailsTray.module.scss';

const EXISTING_NAMES_STALE_TIME = 5 * 60 * 1000;

const sameIds = (a: string[], b: string[]) => a.length === b.length && a.every((id) => b.includes(id));

interface IMenuSetDetailsFormProps {
  menuSet: IMenuSetDetail;
  onClose: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export const MenuSetDetailsForm = ({ menuSet, onClose, onDirtyChange }: IMenuSetDetailsFormProps): ReactElement => {
  // A save can finish after this form has gone (cancelled, or the tray reopened); closing then would close
  // whichever tray is open by that time, so the close is skipped once this form has unmounted.
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );
  const { mutateAsync: updateMenuSet } = useUpdateMenuSet(menuSet.menu_set_id);
  const { trackEvent, captureException } = useUserTracking();
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { isEnabled: rangesEnabled } = useTimetableRangesEnabled();
  const { data: ranges } = useRanges({ enabled: rangesEnabled });
  const { data: menuSetsData } = useMenuSets(50, undefined, undefined, { staleTime: EXISTING_NAMES_STALE_TIME });

  const savedImage = useMemo((): ISavedImage | null => {
    const url = findImageUrl(menuSet.assets);
    const asset = url ? menuSet.assets.find(({ asset_variants }) => asset_variants[0]?.url === url) : undefined;
    return url ? { url, name: asset?.name ?? '' } : null;
  }, [menuSet.assets]);
  const originalRangeIds = useMemo(() => menuSet.ranges.map(({ range_id }) => range_id), [menuSet.ranges]);
  const [newImage, setNewImage] = useState<File | null>(null);

  const form = useForm<IFormData>({
    mode: 'onChange',
    defaultValues: {
      name: menuSet.name,
      consumerName: menuSet.consumer_name ?? '',
      files: null,
      source: originalRangeIds.length > 0 ? EntitySource.RANGES : EntitySource.LIBRARY,
      ranges: originalRangeIds,
    },
  });
  const {
    handleSubmit,
    watch,
    setError,
    formState: { isSubmitting },
  } = form;
  const confirmation = useForm<{ confirmed: boolean }>({ defaultValues: { confirmed: false } });

  const name = watch('name').trim();
  const consumerName = watch('consumerName').trim();
  const source = watch('source');
  const selectedRangeIds = watch('ranges');
  const rangeIds = source === EntitySource.RANGES ? selectedRangeIds : [];

  // The set's own current name doesn't count as a clash.
  const otherMenuSetNames = useMemo(
    () =>
      menuSetsData?.pages.flatMap((page) =>
        page.data
          .filter(({ menu_set_id }) => menu_set_id !== menuSet.menu_set_id)
          .map((other) => other.name.toLocaleLowerCase()),
      ) ?? [],
    [menuSetsData, menuSet.menu_set_id],
  );
  const isNameUnique = (value: string) => !otherMenuSetNames.includes(value.trim().toLocaleLowerCase());
  const isNameTaken = !isNameUnique(name);

  const isNameChanged = name !== menuSet.name;
  // An emptied consumer name isn't sent, so the saved one stays; only a new name counts as a change.
  const isConsumerNameChanged = !!consumerName && consumerName !== (menuSet.consumer_name ?? '');
  const areRangesChanged = !sameIds(rangeIds, originalRangeIds);
  const isImageChanged = newImage !== null;
  const hasChanges = isNameChanged || isConsumerNameChanged || areRangesChanged || isImageChanged;

  useReportTrayDirty(hasChanges, onDirtyChange);

  const isConfirmed = confirmation.watch('confirmed');
  const isMissingRanges = source === EntitySource.RANGES && selectedRangeIds.length === 0;

  const isSaveDisabled =
    isSubmitting || !hasChanges || isNameTaken || !name || isMissingRanges || (areRangesChanged && !isConfirmed);

  const onSubmit = async () => {
    clearError();
    const payload: IUpdateMenuSetRequest = {
      ...(isNameChanged && { name }),
      ...(isConsumerNameChanged && { consumer_name: consumerName }),
      ...(areRangesChanged && { ranges: JSON.stringify(rangeIds) }),
      ...(newImage && { files: newImage }),
    };

    try {
      await updateMenuSet(payload);
      trackEvent('menu_set_details_updated', { menuSetId: menuSet.menu_set_id, fields: Object.keys(payload) });
      showNotification({ status: SnackbarStatus.SUCCESS, message: 'Menu set updated' });
      if (isMountedRef.current) onClose();
    } catch (error) {
      if (error instanceof Error && error.message === UNIQUE_MENU_SET_NAME_ERROR) {
        setError('name', { message: UNIQUE_MENU_SET_NAME_ERROR });
        return;
      }
      captureException(error, { tags: { action: 'menu_set_details_update' } });
      showNotification({
        status: SnackbarStatus.ERROR,
        message: (error instanceof Error && error.message) || 'Something went wrong whilst updating the menu set',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles['content']}>
      <div className={styles['fields']}>
        <MenuDetailsFields
          form={form}
          labels={MENU_SET_DETAILS_LABELS}
          showSource={rangesEnabled}
          ranges={ranges}
          validateName={(value) => isNameUnique(value) || UNIQUE_MENU_SET_NAME_ERROR}
        />
        <MenuSetImageField
          title={MENU_SET_DETAILS_LABELS.image}
          savedImage={savedImage}
          newImage={newImage}
          onChange={setNewImage}
        />
      </div>

      {areRangesChanged && (
        <div className={styles['ranges-change']}>
          <Checkbox
            name="confirmed"
            register={confirmation.register}
            label="I confirm I understand that changes to the ranges may result in unqualified menus being removed from the menu set"
          />
        </div>
      )}

      {errorMessage && <StatusBar message={errorMessage} variant="error" />}

      <div className={styles['button-row']}>
        <ButtonContainer
          direction="column-reverse"
          margin="none"
          left={
            <SecondaryButton buttonStyle="outline" type="button" size="full-width" onClick={onClose}>
              Cancel
            </SecondaryButton>
          }
          right={
            <PrimaryButton type="submit" size="full-width" loading={isSubmitting} disabled={isSaveDisabled}>
              Save
            </PrimaryButton>
          }
        />
      </div>
    </form>
  );
};

export default MenuSetDetailsForm;
